import {
  type Components,
  EVENT_MOUNTED,
  EVENT_RESIZED,
  EVENT_UPDATED,
  EventInterface,
  type Options,
  type SlideComponent,
  type Splide,
} from "@splidejs/splide";

// Constants for better maintainability
const DEFAULT_PER_PAGE = 1;
const ARROW_OFFSET = 2;
const TRANSITION_DURATION = "400ms";
const TRANSITION_EASING = "cubic-bezier(.44,.65,.07,1.01)";

function createArrows(nextLabel: string, prevLabel: string) {
  const arrowClasses = [
    "splide__slide",
    "box-border",
    "flex",
    "items-center",
    "px-0",
    "bg-transparent",
    "border-none",
    "appearance-none",
    "cursor-pointer",
    "text-primary",
  ];
  const nextArrow = document.createElement("button");
  nextArrow.setAttribute("type", "button");
  nextArrow.setAttribute("data-arrow", "next");
  nextArrow.setAttribute("aria-label", nextLabel);
  nextArrow.tabIndex = -1;
  nextArrow.disabled = true;
  nextArrow.dataset.enabled = "false";
  nextArrow.setAttribute("aria-disabled", "true");
  nextArrow.classList.add(...arrowClasses);
  nextArrow.classList.add("justify-end");
  nextArrow.innerHTML = `
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="w-3.5 md:w-4.5 md:h-4.5 h-3.5"
        >
          <path d="M10 19.0312L9.09375 18.1562L16.625 10.625H0.96875V9.375H16.625L9.09375 1.84375L10 0.96875L19.0312 10L10 19.0312Z" fill="currentColor"/>
        </svg>
      `;
  const prevArrow = document.createElement("button");
  prevArrow.setAttribute("type", "button");
  prevArrow.setAttribute("data-arrow", "previous");
  prevArrow.setAttribute("aria-label", prevLabel);
  prevArrow.tabIndex = -1;
  prevArrow.disabled = true;
  prevArrow.dataset.enabled = "false";
  prevArrow.setAttribute("aria-disabled", "true");
  prevArrow.classList.add(...arrowClasses);
  prevArrow.classList.add("justify-start");
  prevArrow.innerHTML = `<svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="w-3.5 rotate-180 md:w-4.5 md:h-4.5 h-3.5"
      >
        <path d="M10 19.0312L9.09375 18.1562L16.625 10.625H0.96875V9.375H16.625L9.09375 1.84375L10 0.96875L19.0312 10L10 19.0312Z" fill="currentColor"/>
      </svg>`;

  return [nextArrow, prevArrow];
}

const SPLIDE_EVENT_MOVING = "moving";

export function InnerArrows(
  nextLabel: string,
  prevLabel: string,
  skipActiveIndex = false
) {
  return function (Splide: Splide, Components: Components, options: Options) {
    const { on } = EventInterface(Splide);
    const { Controller, Elements, Layout, Move, Slides } = Components;
    let observer: MutationObserver;
    const arrowClickHandlers = new Map<HTMLElement, () => void>();

    const isArrowButton = (
      element: HTMLElement
    ): element is HTMLButtonElement =>
      element instanceof HTMLButtonElement &&
      element.hasAttribute("data-arrow");

    const setArrowEnabledState = (
      arrow: HTMLButtonElement,
      isEnabled: boolean
    ) => {
      arrow.dataset.enabled = isEnabled ? "true" : "false";

      if (isEnabled) {
        arrow.tabIndex = 0;
        arrow.disabled = false;
        arrow.removeAttribute("aria-disabled");
        arrow.removeAttribute("aria-hidden");
        arrow.classList.remove("pointer-events-none", "opacity-0");
        return;
      }

      if (document.activeElement === arrow) {
        // Move focus to next logical element before blurring
        const nextFocusable = arrow.nextElementSibling;
        if (
          nextFocusable &&
          nextFocusable instanceof HTMLElement &&
          nextFocusable.tabIndex >= 0
        ) {
          nextFocusable.focus();
        } else {
          // Fallback: find next focusable element in the carousel
          const carousel = arrow.closest(".splide");
          const focusableElements = carousel?.querySelectorAll(
            '[tabindex]:not([tabindex="-1"])'
          );
          if (!focusableElements) return;

          const focusableArray = Array.from(focusableElements).filter(
            (element): element is HTMLElement => element instanceof HTMLElement
          );
          const currentIndex = focusableArray.indexOf(arrow);
          const nextElement = focusableArray[currentIndex + 1];
          if (nextElement) {
            nextElement.focus();
          } else {
            arrow.blur();
          }
        }
      }

      arrow.tabIndex = -1;
      arrow.disabled = true;
      arrow.setAttribute("aria-disabled", "true");
      arrow.setAttribute("aria-hidden", "true");
      arrow.classList.add("pointer-events-none", "opacity-0");
    };

    const handleSwatchKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        // Find the parent data-tbsp-component="colorOption" element and trigger mouseenter
        const swatch = event.currentTarget;
        if (swatch && swatch instanceof HTMLElement) {
          const colorOptionElement = swatch.closest<HTMLElement>(
            '[data-tbsp-component="colorOption"]'
          );
          if (colorOptionElement) {
            colorOptionElement.dispatchEvent(new Event("mouseenter"));
          }
        }
      }
    };

    const setSwatchEnabledState = (swatch: HTMLElement, enabled: boolean) => {
      if (enabled) {
        swatch.tabIndex = 0;
        swatch.removeAttribute("aria-hidden");
        // Add keyboard event listener
        swatch.addEventListener("keydown", handleSwatchKeyDown);
      } else {
        // Before disabling, check if this swatch has focus and blur it
        const activeElement = document.activeElement;
        if (activeElement && swatch.contains(activeElement)) {
          // Move focus to next logical element before blurring
          const nextFocusable = swatch.nextElementSibling;
          if (
            nextFocusable &&
            nextFocusable instanceof HTMLElement &&
            nextFocusable.tabIndex >= 0
          ) {
            nextFocusable.focus();
          } else {
            // Fallback: find next focusable element in the carousel
            const carousel = swatch.closest(".splide");
            const focusableElements = carousel?.querySelectorAll(
              '[tabindex]:not([tabindex="-1"])'
            );
            if (focusableElements) {
              const focusableArray = Array.from(focusableElements).filter(
                (element): element is HTMLElement =>
                  element instanceof HTMLElement
              );
              const currentIndex = focusableArray.indexOf(swatch);
              const nextElement = focusableArray[currentIndex + 1];
              if (nextElement) {
                nextElement.focus();
              } else {
                swatch.blur();
              }
            } else {
              swatch.blur();
            }
          }
        }

        swatch.tabIndex = -1;
        swatch.setAttribute("aria-hidden", "true");
        // Remove keyboard event listener
        swatch.removeEventListener("keydown", handleSwatchKeyDown);
      }
    };

    const getArrowSlides = () =>
      Slides.filter((slideComponent) => {
        const { slide } = slideComponent;
        return (
          slide.hasAttribute("data-arrow") &&
          !slide.classList.contains("splide__slide--clone")
        );
      });

    const removeArrowClickHandler = (slide: HTMLElement) => {
      const handler = arrowClickHandlers.get(slide);
      if (handler) {
        slide.removeEventListener("click", handler);
        arrowClickHandlers.delete(slide);
      }
    };

    const getSwatchSlides = () => {
      return Slides.filter((slideComponent) => {
        const { slide } = slideComponent;
        const hasColorOption =
          slide.hasAttribute("data-tbsp-component") &&
          slide.getAttribute("data-tbsp-component") === "colorOption";
        const isNotClone = !slide.classList.contains("splide__slide--clone");
        return hasColorOption && isNotClone;
      });
    };

    const getArrowSlidesByType = (type: string) =>
      getArrowSlides()
        .filter(({ slide }) => slide.getAttribute("data-arrow") === type)
        .sort((a, b) => a.index - b.index);

    const getNonArrowSlideCount = () =>
      Slides.filter((slideComponent) => {
        const { slide } = slideComponent;
        return (
          !slide.hasAttribute("data-arrow") &&
          !slide.classList.contains("splide__slide--clone")
        );
      }).length;

    const getBasePerPage = () =>
      Math.max(
        DEFAULT_PER_PAGE,
        (Splide.options.perPage || DEFAULT_PER_PAGE) - DEFAULT_PER_PAGE
      );

    const applyArrowVisibilityForPage = (page: number) => {
      const totalSlides = getNonArrowSlideCount();
      const perPage = getBasePerPage();
      const totalPages = Math.max(1, Math.ceil(totalSlides / perPage));
      const lastPage = totalPages - 1;

      const nextArrows = getArrowSlidesByType("next");
      const prevArrows = getArrowSlidesByType("previous");

      nextArrows.forEach(({ slide }, index) => {
        if (!isArrowButton(slide)) return;
        const isEnabled = page < lastPage && index === page;
        setArrowEnabledState(slide, isEnabled);
      });

      prevArrows.forEach(({ slide }, index) => {
        if (!isArrowButton(slide)) return;
        const isEnabled = page > 0 && index === page - 1;
        setArrowEnabledState(slide, isEnabled);
      });
    };

    const applySwatchVisibilityForPage = (page: number) => {
      const swatchSlides = getSwatchSlides();
      const perPage = Number(Splide.options.perPage) || DEFAULT_PER_PAGE;
      const startIndex = page * perPage;
      const endIndex = startIndex + perPage;

      swatchSlides.forEach(({ index, slide }) => {
        // The focusable element is the first div inside the slide
        const swatchElement = slide.querySelector("div");
        if (swatchElement && swatchElement instanceof HTMLElement) {
          const isVisible = index >= startIndex && index < endIndex;

          if (isVisible) {
            slide.removeAttribute("aria-hidden");
            slide.removeAttribute("tabindex");
          } else {
            slide.setAttribute("aria-hidden", "true");
            slide.setAttribute("tabindex", "-1");
          }

          setSwatchEnabledState(swatchElement, isVisible);
        }
      });
    };

    const updateArrowAccessibility = () => {
      const currentPage = getPage(Controller.getIndex());
      applyArrowVisibilityForPage(currentPage);
      applySwatchVisibilityForPage(currentPage);
    };

    const getListSize = () => {
      const totalPadding = Layout.getPadding(false) + Layout.getPadding(true);
      const perPage = Number(Splide.options.perPage) || DEFAULT_PER_PAGE;
      const totalGap = 1 * (perPage - 1);
      return Layout.listSize() + totalPadding + totalGap;
    };

    const getPage = (index?: number) => {
      const currentIndex = index ?? Controller.getIndex();
      const { perPage } = Splide.options;
      return Math.floor(currentIndex / (perPage || 1));
    };

    const moveByIndex = (index: number) => {
      const listSize = getListSize();
      const newPage = getPage(index);
      Splide.emit(SPLIDE_EVENT_MOVING);
      Move.translate(-(newPage * listSize));
      return newPage;
    };

    const attachArrowClick = () => {
      const arrowSlides = Slides.filter((slide) => {
        if (slide.slide.getAttribute("data-arrow")) {
          return true;
        }

        return false;
      });

      const nextPage = (slide: SlideComponent) => () => {
        if (Controller.isBusy()) return;
        const { index } = slide;
        const newIndex = index + ARROW_OFFSET;
        const newPage = moveByIndex(newIndex);
        if (!skipActiveIndex) {
          Controller.setIndex(newIndex);
          Splide.emit("active");
        }
        applyArrowVisibilityForPage(newPage);
        applySwatchVisibilityForPage(newPage);
      };

      const previousPage = (slide: SlideComponent) => () => {
        if (Controller.isBusy()) return;
        const { perPage } = options;
        const { index } = slide;
        const newIndex =
          index - ARROW_OFFSET < (perPage || DEFAULT_PER_PAGE)
            ? 0
            : index - ARROW_OFFSET;
        const newPage = moveByIndex(newIndex);

        if (!skipActiveIndex) {
          Controller.setIndex(newIndex);
          Splide.emit("active");
        }
        applyArrowVisibilityForPage(newPage);
        applySwatchVisibilityForPage(newPage);
      };

      arrowSlides.forEach((arrowSlide) => {
        const { slide } = arrowSlide;
        const arrowType = slide.getAttribute("data-arrow");

        removeArrowClickHandler(slide);

        let handler: (() => void) | undefined;
        if (arrowType === "next") {
          handler = nextPage(arrowSlide);
        } else if (arrowType === "previous") {
          handler = previousPage(arrowSlide);
        }

        if (handler) {
          slide.addEventListener("click", handler);
          arrowClickHandlers.set(slide, handler);
        }
      });
    };

    const updatePerPage = () => {
      options.perPage = (options.perPage || 1) + 1;
      Splide.refresh();
    };

    const destroyArrows = () => {
      getArrowSlides().forEach(({ slide }) => {
        removeArrowClickHandler(slide);
      });
      Slides.remove("[data-arrow]");
    };

    const renderArrows = () => {
      destroyArrows();
      if (Slides.getLength() + 1 <= (options.perPage || 0)) {
        return;
      }

      let newSlideCounter = 1;
      const size = Slides.getLength();
      Slides.forEach((_slide, index) => {
        if (!index || index > size - 1) {
          newSlideCounter++;
          return;
        }

        if (newSlideCounter % (options.perPage || 0) === 0) {
          const [nextArrow, prevArrow] = createArrows(nextLabel, prevLabel);
          Slides.add([nextArrow, prevArrow], newSlideCounter - 1);
          newSlideCounter += 2;
        }

        newSlideCounter++;
      });

      updateArrowAccessibility();
    };

    const resetStartingPosition = () => {
      // Bypass start option and move slider list based on current page instead of current index
      const initialIndex = Controller.getIndex();
      Elements.list.style.opacity = "0";
      Controller.setIndex(0);
      if (!observer) {
        observer = new MutationObserver((mutationList) => {
          for (const mutation of mutationList) {
            if (
              mutation.type === "childList" &&
              (<HTMLElement>mutation.target).querySelector("[data-arrow]")
            ) {
              const originalSlides = Slides.filter(({ slide }) => {
                if (!slide.getAttribute("data-arrow")) {
                  return true;
                }
                return false;
              });

              const activeSlide = originalSlides.find(({ slide }, index) => {
                if (slide.hasAttribute("data-arrow")) return false;
                if (index === initialIndex) return true;
                return false;
              });

              if (!activeSlide) return;
              moveByIndex(activeSlide.index);
              Elements.list.style.opacity = "100%";
              Splide.emit("active");
              observer.disconnect();
            }
          }
        });

        observer.observe(Elements.list, { childList: true, subtree: true });
      }
    };

    const flexCenterSlides = () => {
      const size = Slides.getLength();
      const endPageSlides = size - (size % (options.perPage || 1));
      Slides.forEach(({ index, slide }) => {
        if (slide.getAttribute("data-arrow")) return false;
        if (index < (options.perPage || 1) || index >= endPageSlides) {
          slide.classList.remove("justify-center");
        } else {
          slide.classList.add("justify-center");
        }
      });
    };

    const setMinimumSlideWidth = () => {
      Slides.forEach(({ slide }) => {
        slide.classList.add("min-w-fit");
      });
    };

    function mount() {
      if (options.isNavigation) {
        console.warn(
          "Avoid using InnerArrows Extension with isNavigation option."
        );
      }

      updatePerPage();
      resetStartingPosition();
      renderArrows();
      setMinimumSlideWidth();

      on(SPLIDE_EVENT_MOVING, () => {
        Elements.list.style.transition = `transform ${TRANSITION_DURATION} ${TRANSITION_EASING}`;
      });

      on(EVENT_MOUNTED, () => {
        attachArrowClick();
        flexCenterSlides();
        updateArrowAccessibility();
      });

      on(EVENT_RESIZED, () => {
        moveByIndex(Controller.getIndex());
        updateArrowAccessibility();
      });

      on(EVENT_UPDATED, () => {
        updatePerPage();
        resetStartingPosition();
        renderArrows();
        attachArrowClick();
        flexCenterSlides();
        setMinimumSlideWidth();
        updateArrowAccessibility();
      });
    }

    const cleanupSwatchEventListeners = () => {
      const swatchSlides = getSwatchSlides();
      swatchSlides.forEach(({ slide }) => {
        const swatchElement = slide.querySelector("div");
        if (swatchElement && swatchElement instanceof HTMLElement) {
          swatchElement.removeEventListener("keydown", handleSwatchKeyDown);
        }
      });
    };

    const cleanupArrowEventListeners = () => {
      getArrowSlides().forEach(({ slide }) => {
        removeArrowClickHandler(slide);
      });
    };

    function destroy() {
      if (observer) {
        observer.disconnect();
      }
      cleanupSwatchEventListeners();
      cleanupArrowEventListeners();
    }

    return {
      mount,
      destroy,
    };
  };
}

export function RadioSlides(Splide: Splide, Components: Components) {
  const { on } = EventInterface(Splide);
  const { Controller, Slides } = Components;
  function mount() {
    selectRadio();
    on("active", selectRadio);
  }

  function selectRadio() {
    const currentIndex = Controller.getIndex();
    const activeSlide = Slides.getAt(currentIndex);
    if (!activeSlide) return;
    const activeRadio = activeSlide.slide.querySelector<HTMLInputElement>(
      "input[type='radio']"
    );
    const form = activeSlide.slide.closest<HTMLFormElement>("form");
    if (!activeRadio) return;
    if (!form) throw new Error("Radios in RadioSlides must be part of a form");
    const radios = form.querySelectorAll<HTMLInputElement>(
      `input[type="radio"][name="${activeRadio.name}"]`
    );
    [...radios].forEach((radio) => {
      radio.checked = false;
    });
    activeRadio.checked = true;

    const forceChangeEvent = new Event("change", { bubbles: true });
    activeRadio.dispatchEvent(forceChangeEvent);
  }

  return {
    mount,
  };
}
